import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { getUserDetails, requestReport } from "../../service/Report";
import { Spinner } from "../../Spinner";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ReactSelector from "../common/CommonReactSelector";
import datePickerPopper from "../common/DatePickerPopper";
import ModalCloseButton from "../common/ModalCloseButton";
import { components } from 'react-select';

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

const parseYmd = (s) => {
  if (!s) return null;
  const p = String(s).split("-").map(Number);
  if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
  return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function RequestReport({
  open,
  setOpen,
  handleReportRequestSuccess,
}) {
  const [ReportType, setReportType] = useState("");
  const [UserType, setUserType] = useState("");
  const [userlist, setUserList] = useState("");
  const [selecteduser, setSelectedUser] = useState();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const cancelButtonRef = useRef(null);
  const { theme, selectedOrgId, langMode } = useContext(AppContext);
  const todayPicker = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const reportType = [
    { id: "VEHICLES", value: "VEHICLES" },
    { id: "INVENTORY", value: "INVENTORY" },
  ];
  const userType = [
    { id: "admin", value: "admin" },
    { id: "office_user", value: "office_user" },
  ];

  const status = [
    { id: "0", value: "pending" },
    { id: "1", value: "ready to download" },
  ];

  const reportTypeOptions = reportType.map((type) => ({
    value: type.id,
    label: type.value,
  }));

  const handleReportType = (selectedOption) => {
    if (selectedOption) {
      setReportType(selectedOption.value);
    }
  };
  const userTypeOptions = userType.map((type) => ({
    value: type.id,
    label: type.value,
  }));

  const handleUserType = (selectedOption) => {
    if (selectedOption) {
      setUserType(selectedOption.value);
      setSelectedUser(null); // Clear selected user when user type changes
    }
  };

  const userOptions = userlist
    ? userlist.map((user) => ({
      value: user.userId,
      label: user.userName,
    }))
    : [];

  const handleSelectUser = (selectedOption) => {
    if (selectedOption) {
      setSelectedUser(selectedOption.value);
    }
  };

  useEffect(() => {
    if (selectedOrgId && UserType) {
      const loadUserDetails = async () => {
        try {
          const users = await getUserDetails(selectedOrgId, UserType);
          setUserList(users);
        } catch (error) {
          console.error("Error loading user details:", error);
        }
      };
      loadUserDetails();
    }
  }, [selectedOrgId, UserType]);

  const handleSubmit = async () => {
    if (!ReportType || !UserType || !startDate || !endDate) {
      toast.error(translations[langMode].allfieldarerequired);
      return;
    }
    try {
      setIsLoading(true)
      await requestReport({
        selecteduser,
        UserType,
        ReportType,
        startDate,
        endDate,
      });
      setIsLoading(false)
      handleReportRequestSuccess();
      toast.success(translations[langMode].reportreqsuc);
      setOpen(false);
      setUserType("");
      setReportType("");
      setStartDate("");
      setEndDate("");
      setSelectedUser("");
    } catch (error) {
      setIsLoading(false)
      toast.error(translations[langMode].errorequestreport);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[100]"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity `}
          />
        </Transition.Child>
        <div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-2.5 sm:p-4 text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={`relative transform overflow-hidden rounded-xl sm:rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 w-full max-w-lg max-h-[88vh] flex flex-col ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
              >
                {isLoading && <Spinner />}
                <div className="shrink-0 flex justify-between items-center px-4 sm:px-5 py-3.5 sm:py-4 border-b border-slate-100">
                  <h4
                    className={`text-base font-semibold uppercase
          ${theme === "dark" ? "text-white" : "text-gray-800"}
          ${theme === "high-contrast" ? "text-black" : "text-gray-700"}`}
                  >
                    {translations[langMode].reportbtn}
                  </h4>
                  <ModalCloseButton onClick={() => setOpen(false)} />
                </div>
                <div
                  className={`flex-1 min-h-0 overflow-y-auto p-5 grid grid-cols-1 gap-5 sm:grid-cols-2 ${theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                    }`}
                >
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-900 dark:text-white">{translations[langMode].reportType}</label>
                    <ReactSelector
                      options={reportTypeOptions}
                      placeholder={translations[langMode].reportType}
                      onChange={handleReportType}
                      components={Placeholder}
                      value={reportTypeOptions.find(option => option.value === ReportType)}
                      height="46px"
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-900 dark:text-white">{translations[langMode].userType}</label>
                    <ReactSelector
                      options={userTypeOptions}
                      placeholder={translations[langMode].userType}
                      onChange={handleUserType}
                      components={Placeholder}
                      value={userTypeOptions.find(option => option.value === UserType)}
                      height="46px"
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-gray-900 dark:text-white">{translations[langMode].user}</label>
                    <ReactSelector
                      options={userOptions}
                      placeholder={translations[langMode].user}
                      onChange={handleSelectUser}
                      components={Placeholder}
                      value={userOptions.find(option => option.value === selecteduser)}
                      height="46px"
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div className="col-span-1 sm:col-span-2 flex flex-col gap-1.5">
                    <div className="flex w-full min-w-0 items-center gap-2 sm:gap-4">
                      <div className="flex-1 min-w-0 flex flex-col gap-y-1.5">
                        <label className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{translations[langMode].fromdate}</label>
                        <DatePicker
                          selected={parseYmd(startDate)}
                          onChange={(date) => {
                            setStartDate(date ? formatYmd(date) : "");
                            if (date && parseYmd(endDate) && parseYmd(endDate) < date) {
                              setEndDate(formatYmd(date));
                            }
                          }}
                          placeholderText={translations[langMode].fromdate}
                          maxDate={todayPicker}
                          selectsStart
                          startDate={parseYmd(startDate)}
                          endDate={parseYmd(endDate)}
                          className={`w-full h-10 px-2.5 !rounded-lg text-xs sm:text-sm border border-gray-300 box-border ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                          calendarClassName="!rounded-lg shadow-lg"
                          popperPlacement="bottom-start"
                          popperContainer={datePickerPopper}
                        />
                      </div>
                      <span className="text-slate-400 text-sm hidden sm:inline self-end mb-2">→</span>
                      <div className="flex-1 min-w-0 flex flex-col gap-y-1.5">
                        <label className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{translations[langMode].todate}</label>
                        <DatePicker
                          selected={parseYmd(endDate)}
                          onChange={(date) => setEndDate(date ? formatYmd(date) : "")}
                          placeholderText={translations[langMode].todate}
                          minDate={parseYmd(startDate)}
                          maxDate={todayPicker}
                          selectsEnd
                          startDate={parseYmd(startDate)}
                          endDate={parseYmd(endDate)}
                          disabled={!startDate}
                          className={`w-full h-10 px-2.5 !rounded-lg text-xs sm:text-sm border border-gray-300 box-border ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                          calendarClassName="!rounded-lg shadow-lg"
                          popperPlacement="bottom-start"
                          popperContainer={datePickerPopper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={`shrink-0 sticky bottom-0 bg-white flex w-full justify-end items-center gap-2 sm:gap-3 border-t px-4 sm:px-5 py-3 sm:py-4 ${theme === "dark"
                    ? "dark-mode"
                    : theme === "high-contrast"
                      ? "high-contrast"
                      : ""
                    }`}
                >
                  <CommonButton colorIntent="danger"
                    onClick={() => setOpen(false)}
                    variant="danger"
                    theme={theme}
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {translations[langMode].cancel}
                  </CommonButton>
                  <CommonButton colorIntent="success"
                    onClick={handleSubmit}
                    variant="primary"
                    theme={theme}
                    disabled={isLoading}
                    wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                    className="w-full !h-10 !rounded-lg justify-center"
                  >
                    {isLoading ? translations[langMode].Requesting : translations[langMode].request}
                  </CommonButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
